import { Autocomplete } from '@mui/material';
import { TextField, Popper } from '@mui/material';
import css from '../../Form/FormWrapper/FormWrapper.module.scss';
import classNames from 'classnames';
import { BaseAutocompleteProps } from './BaseAutocomplete.types';
import { HTMLAttributes, ReactNode } from 'react';
import { GMFormWrapper } from '../../Form';

export const BaseAutocomplete = <T,>({
    id,
    title,
    label,
    ariaLabel,
    labelClass,
    ref,
    inputRef,
    inputValue,
    disabled,
    touched,
    error,
    options,
    filterOptions,
    getOptionLabel,
    onOptionSelected,
    renderOption,
    onBlur,
    onInputChange,
    onFocus,
    capsLock,
    readOnly,
    freeSolo,
    forceBottomPopper,
    openOnFocus = true,
    isOptionEqualToValue,
    value,
    selectOnFocus,
    fullWidth,
    seventyFivePercentWidth,
}: BaseAutocompleteProps<T>) => {
    const handleRenderOption = (props: HTMLAttributes<HTMLLIElement>, option: T): ReactNode => {
        return (
            <li {...props} key={`ac-option-${props.id}`}>
                {renderOption && renderOption(option)}
            </li>
        );
    };
    return (
        <GMFormWrapper fullWidth={fullWidth} seventyFivePercentWidth={seventyFivePercentWidth}>
            <Autocomplete
                aria-label={ariaLabel}
                id={id}
                title={title}
                ref={ref}
                disabled={disabled}
                disablePortal
                includeInputInList
                classes={{
                    root: css.autoComplete,
                    input: css.autoCompleteInput,
                    popper: css.autocompletePopper,
                    option: css.autocompleteOption,
                }}
                options={options}
                getOptionLabel={getOptionLabel}
                filterOptions={filterOptions}
                ListboxProps={{
                    style: {
                        maxHeight: '300px',
                    },
                }}
                renderOption={renderOption ? handleRenderOption : undefined}
                onChange={(event, value) => {
                    onOptionSelected && onOptionSelected(value);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={inputRef}
                        variant={'standard'}
                        fullWidth
                        disabled={disabled}
                        label={label}
                        InputLabelProps={{
                            classes: {
                                root: classNames(css.label, css.autoCompleteLabel, labelClass),
                                focused: css.focused,
                                error: css.error,
                            },
                        }}
                        InputProps={{
                            ref: params.InputProps.ref,
                            classes: {
                                root: classNames(params.InputProps.className, css.input),
                                error: css.error,
                                input: css.baseInput,
                            },
                            startAdornment: params.InputProps.startAdornment,
                            endAdornment: params.InputProps.endAdornment,
                        }}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: 'one-time-code',
                            readOnly: readOnly,
                            style: {
                                textTransform: !!capsLock ? 'uppercase' : 'unset',
                            },
                        }}
                        error={touched && !!error}
                        helperText={touched ? error : undefined}
                        FormHelperTextProps={{
                            classes: {
                                root: css.errorMessage,
                            },
                        }}
                    />
                )}
                PopperComponent={(popperProps) =>
                    (forceBottomPopper ? (
                        <Popper
                            {...popperProps}
                            placement={'bottom-start'}
                            modifiers={[{
                                name: 'flip',
                                enabled: false,
                            }]}
                            popperOptions={{
                                placement: 'bottom',
                            }}
                        />
                    ) : (
                        <Popper {...popperProps} placement={'top-start'} />
                    ))
                }
                onFocus={onFocus}
                onBlur={() => onBlur && onBlur()}
                freeSolo={freeSolo}
                openOnFocus={openOnFocus}
                inputValue={inputValue}
                onInputChange={(event, value) => onInputChange && onInputChange(event, value)}
                forcePopupIcon
                isOptionEqualToValue={isOptionEqualToValue}
                value={value}
                selectOnFocus={selectOnFocus}
            />
        </GMFormWrapper>
    );
};